// DistinctNote — the 2–4 line "what is statistically unusual about this flat" note on the card face
// (owner brief 2026-09-06, migration 0894).
//
// The DATABASE decided what is unusual (refresh_property_distinctiveness_v1: comparable cohort →
// price position → feature prevalence → a deterministic top-2 with sample sizes and confidence), and
// the WRITER (scripts/distinct_writer.js on the box) turned only those selected findings into prose.
// This component renders the prose and says who wrote it — nothing here reasons about the flat.
//
// ⚠️ ABSENCE DRAWS NOTHING. `nothing_unusual` and `insufficient_evidence` are real verdicts and both
// render as no note: a card without a note is an ordinary flat, not a broken card. The caller keeps
// the previous AgentFacts row in that case (owner 2026-09-06: the note REPLACES that row when it
// exists).
//
// The ✦ mark is Orpheus's `authored` form (AiMark): the whole block is the artefact, so there is no
// per-claim quote — the hover names the writer, the model version and the date instead. The
// structured facts ride in `title` so an agent can check the number behind the sentence in one hover.
import React from 'react'
import AiMark from './AiMark.jsx'

// One (cluster, intent) row per advert; a flat with both a rent and a sale advert carries two rows
// and the card shows the one for ITS listing. Falls back to the first row only when the intent is
// unknown (a manual card), never across intents.
export function pickDistinct(rows, intent) {
  if (!Array.isArray(rows) || !rows.length) return null
  return rows.find(r => r.intent === intent) || (intent ? null : rows[0])
}

function factsTitle(row) {
  const parts = []
  for (const f of row.selected || []) {
    // The price fact now carries WHAT IT WAS MEASURED AGAINST, and the hover has to say so — a
    // reader who cannot tell a street cohort of 12 from a district cohort of 800 cannot judge the
    // number. `geo_level` is street | district | parent (0916), `price_basis` says whether the
    // subject's price is one advert or the median of several (0913), and `adjusted_for` records the
    // condition restatement (0915).
    if (f.kind === 'price') parts.push([
      `${f.deviation_pct > 0 ? '+' : ''}${f.deviation_pct}%`,
      `${f.geo_level || 'district'} n=${f.n}`,
      f.price_basis && f.price_basis !== 'single advert' ? f.price_basis : null,
      f.spread_ratio > 1.25 ? `⚠ adverts differ ${f.spread_ratio}×` : null,
      f.adjusted_for ? 'condition-adjusted' : null,
      f.confidence,
    ].filter(Boolean).join(' · '))
    else if (f.kind === 'gap') parts.push(`⚠ ${f.label_en || f.code} · ${f.confidence}`)
    else if (f.kind === 'opportunity') parts.push(`↗ ${f.label_en || f.code} · ${f.confidence}`)
    else if (f.kind === 'missing') parts.push(`− ${f.label_en || f.code} (baseline ${f.baseline_pct}%) · n=${f.n_known} · ${f.confidence}`)
    else if (f.kind === 'seller') parts.push(`◆ ${f.label_en || f.code} · ${f.confidence}`)
    else if (f.kind === 'risk' || f.type === 'property_risk') parts.push(`⚠ ${f.label_en || f.code}${f.prevalence_pct != null ? `: ${f.prevalence_pct}%` : ''}${f.n_known ? ` · n=${f.n_known}` : ''} · ${f.confidence}`)
    else parts.push(`${f.label_en || f.code}: ${f.prevalence_pct}% · n=${f.n_known} · ${f.confidence}`)
  }
  return parts.join(' — ')
}

export default function DistinctNote({ row, className = '' }) {
  // 2026-09-07: a note is shown whenever one exists. "nothing unusual" and "cannot be priced
  // because …" are findings too; gating on verdict === 'distinct' hid three of the twenty
  // hand-written notes (owner: "comment is not there").
  if (!row) return null
  // Georgian on the card whatever the interface language (owner 2026-09-06: "let it write in Georgian
  // too" — the Desk was in English and showed the English text). English rides the hover.
  const text = row.text_ka || row.text_en
  if (!text) return null
  const mark = { tier: 'ai', agent: 'orpheus', action: 'authored', version: row.text_version || null, date: row.text_at || null }
  // Owner ruling 2026-09-06: "when you write any desc it must contain writer Orpheus version V0."
  // The label is VISIBLE on the note, not only in the mark's hover — an agent reading a comment must
  // be able to tell which writer produced it without hovering. `text_version` is that display label
  // (migration 0911); the engineering detail lives in text_prompt_version and is never shown.
  const writer = row.text_version || 'Orpheus V0'
  return (
    <div className={`flex items-start gap-1.5 text-sm ${className}`} title={[row.text_en, factsTitle(row)].filter(Boolean).join('\n')} data-distinct-note>
      {/* Wraps anywhere, writer label included (owner phone screenshot 2026-09-17): the label
          "Orpheus V0 · ხელით შედგენილი · glossary-2026-09-10" was whitespace-nowrap, one unbreakable run
          that pushed past the card's right edge on a phone. It now wraps like the text it follows. */}
      <p className="whitespace-pre-line leading-snug min-w-0 flex-1 [overflow-wrap:anywhere]">
        {text}
        <span className="text-[11px] text-muted-foreground/70 ml-1.5" data-writer-version>· {writer}</span>
      </p>
      <AiMark mark={mark} className="shrink-0 mt-0.5" />
    </div>
  )
}
